import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button as DSButton } from '../../../components/DesignSystem/components/Button';
import { Heading, Text } from '../../../components/DesignSystem/components/Typography';
import { Input } from '../../../components/DesignSystem/components/Input';
import { Navbar } from '../../../components/DesignSystem/components/Navbar';
import { Logo } from "../../../components/DesignSystem/components/Logo";
import { ArrowLeft, Mail, ArrowRight } from "lucide-react";
import '../../../pages/Home/Home.css'; // Import landing page css for header
import './Login.css';
import { Footer } from '../../../components/DesignSystem/components/Footer';

// Login Imports
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleLogin } from "@react-oauth/google";
import { API_URL } from "../../../api";
import { useCookies } from "../../../hooks/useCookies";
import { handleUserNavigation } from "../../../utils/handleUserRedirect";
import { Spinner } from "react-bootstrap";
import { loginEmailOTP, loginPhoneOTP } from "../../../actions/auth";
import { useRateLimit } from "../../../hooks/useRateLimit";
import { SecureStorage } from "../../../security/SecureStorage";

import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../../../components/sign-in/signin.css";

const DEFAULT_COUNTRY = "CA";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login = () => {
    const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const recaptchaRef = useRef(null);
    
    const navigate = useNavigate();
    const cookies = useCookies();
    const [errorMessage, setErrorMessage] = useState("");
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isPhoneLoading, setIsPhoneLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    
    // Page load state for smooth transition
    const [isPageLoaded, setIsPageLoaded] = useState(false);

    useEffect(() => {
        setIsPageLoaded(true);
    }, []);

    // Phone State
    const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
    const [isDetectingCountry, setIsDetectingCountry] = useState(false);
    
    const handleCountrySelect = useCallback((code) => {
        setSelectedCountry(code);
        setErrorMessage("");
    }, []);

    // Implement precise, silent IP-based geolocation dynamically
    useEffect(() => {
        let isMounted = true;
        const detectSilentlyByIP = async () => {
            setIsDetectingCountry(true);
            try {
                const response = await fetch('https://ipapi.co/json/');
                if (response.ok) {
                    const data = await response.json();
                    if (data.country_code && isMounted) {
                        handleCountrySelect(data.country_code);
                        setIsDetectingCountry(false);
                        return;
                    }
                }
                
                // Secondary Fallback Service to ensure it resolves practically 100% of the time seamlessly
                const fallbackResponse = await fetch('https://get.geojs.io/v1/ip/country.json');
                if (fallbackResponse.ok) {
                    const fallbackData = await fallbackResponse.json();
                    if (fallbackData.country && isMounted) {
                        handleCountrySelect(fallbackData.country);
                    }
                }
            } catch (error) {
                console.warn("Silent Country Detection failed:", error);
            } finally {
                if (isMounted) setIsDetectingCountry(false);
            }
        };

        detectSilentlyByIP();

        return () => { isMounted = false; };
    }, [handleCountrySelect]);

    const handlePhoneNumberChange = useCallback((value) => {
        setPhone(value || "");
        setErrorMessage("");
    }, [setPhone]);

    // Rate limiting hook
    const {
        isRateLimited,
        timeRemaining,
        handleRateLimitError,
        formatTimeRemaining,
        getRateLimitMessage
    } = useRateLimit('signin-email');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loginMethod === 'email') {
            if (isEmailLoading || isGoogleLoading || isRateLimited) return;

            if (!email || !EMAIL_REGEX.test(email)) {
                setErrorMessage("Please enter a valid email address.");
                return;
            }

            setIsEmailLoading(true);
            setErrorMessage("");

            try {
                if (recaptchaRef.current) recaptchaRef.current.reset();
                const token = await recaptchaRef.current?.executeAsync();

                // OPTIMISTIC NAVIGATION
                SecureStorage.setSessionData("pendingAuthEmail", email);
                navigate("/signinemailotp", { state: { email: email } });
                setEmail("");

                // BACKGROUND API CALL
                loginEmailOTP({
                    email,
                    recaptchaToken: token
                }).catch((e) => console.error(e));

            } catch (error) {
                // Ignore API errors for UX purposes as requested by optimistic UI flow
                console.error("An unexpected error occurred verifying your request", error);
            } finally {
                setIsEmailLoading(false);
            }
        } else if (loginMethod === 'phone') {
            if (isPhoneLoading || isGoogleLoading || isRateLimited) return;

            if (!phone || !isValidPhoneNumber(phone)) {
                setErrorMessage("*Please enter a valid phone number.");
                return;
            }

            setIsPhoneLoading(true);
            setErrorMessage("");

            try {
                if (recaptchaRef.current) recaptchaRef.current.reset();
                const token = await recaptchaRef.current?.executeAsync();

                // OPTIMISTIC NAVIGATION
                SecureStorage.setSessionData("pendingAuthPhone", phone);
                navigate(`/signinphoneotp`, { state: { phone: phone } });

                // BACKGROUND API CALL
                loginPhoneOTP({
                    phone: phone,
                    recaptchaToken: token
                }).catch((e) => console.error(e));
                
            } catch (error) {
                console.error("An unexpected error occurred verifying your request.", error);
            } finally {
                setIsPhoneLoading(false);
            }
        }
    };

    const login = useGoogleLogin({
        onSuccess: async ({ access_token }) => {
            try {
                setIsGoogleLoading(true);
                const userInfoRes = await fetch(
                    "https://www.googleapis.com/oauth2/v3/userinfo",
                    {
                        headers: { Authorization: `Bearer ${access_token}` },
                    }
                );
                const userInfo = await userInfoRes.json();

                const loginRes = await fetch(`${API_URL}user/login/email`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: userInfo.email, usingGoogle: true }),
                });

                const result = await loginRes.json();

                if (loginRes.ok && result.data?.length > 0) {
                     if (!result || !result?.data?.length) {
                         setIsGoogleLoading(false);
                         setErrorMessage("Please signup first.");
                         return;
                     }  
                     const user = result.data[0];
                     cookies.setCookie("token", result?.token, 30);
                     cookies.setCookie("userId", user?.user_id);
                     cookies.setCookie("verified", user?.email_verified_at ? true:false);
                     if ([10, 15, 20, 30].includes(user.approval)) {
                         cookies.setCookie("Name", user.first_name, 20);
                     }
                     await handleUserNavigation(result.data, navigate, setIsGoogleLoading,'');
                } else {
                    setErrorMessage("Login failed. Please try again.");
                }
            } catch (err) {
                setErrorMessage("An error occurred. Please try again.");
            } finally {
                setIsGoogleLoading(false);
            }
        },
        onError: () => setErrorMessage("Google login failed. Please try again."),
    });

    return (
        <div 
            className="login-page"
            style={{ opacity: isPageLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            <div className="login-split-container">
                {/* Left Side: Hero Image */}
                <div className="login-hero-side group">
                    <div className="login-hero-overlay"></div>
                    <img 
                        alt="Happy couple laughing together" 
                        className="login-hero-img" 
                        src={require("../../../assets/images/img_login.png")}
                    />
                    <div className="login-hero-content">
                        <div className="login-hero-header">
                            <Logo showText={false} />
                            <h2 className="login-hero-title">myTamilDate</h2>
                        </div>
                        <p className="login-hero-subtitle">
                            Join the most exclusive community of singles looking for meaningful connections.
                        </p>
                    </div>
                </div>

                {/* Right Side: Form */}
                <div className="login-form-side">
                    {/* Main Auth Card */}
                    <div className="login-form-inner">
                        {/* Mobile Back Button */}
                        <div className="login-mobile-back">
                            <button type="button" onClick={() => window.history.back()} className="back-btn-mobile" aria-label="Go back">
                                <ArrowLeft className="shrink-0" size={24} />
                            </button>
                        </div>
                        <div className="form-header">
                            <h2 className="login-heading-new">Welcome back</h2>
                            <p className="login-subtitle-new">Find your connection today.</p>
                        </div>

                        <form className="login-form" onSubmit={handleSubmit}>
                            {/* Login Method Toggle */}
                            <div className="login-method-toggle">
                                <button
                                    className={`toggle-btn ${loginMethod === 'email' ? 'active' : ''}`}
                                    type="button"
                                    onClick={() => setLoginMethod('email')}
                                >
                                    Email
                                </button>
                                <button
                                    className={`toggle-btn ${loginMethod === 'phone' ? 'active' : ''}`}
                                    type="button"
                                    onClick={() => setLoginMethod('phone')}
                                >
                                    Phone<span className="mobile-hide-text"> Number</span>
                                </button>
                            </div>

                            <div className="form-group">
                                <label className="login-label-new" htmlFor="login-input">
                                    {loginMethod === 'email' ? 'EMAIL ADDRESS' : 'PHONE NUMBER'}
                                </label>
                                
                                {loginMethod === 'email' ? (
                                    <div className="relative w-full">
                                        <div className={`premium-phone-input ${errorMessage ? 'error' : ''} ${isEmailLoading || isGoogleLoading || isRateLimited ? 'disabled' : ''}`}>
                                            <div className="PhoneInputCountry" style={{ cursor: 'default' }}>
                                                <Mail size={20} color="#94a3b8" />
                                            </div>
                                            <input
                                                type="email"
                                                id="login-input"
                                                className="PhoneInputInput"
                                                placeholder="e.g. name@email.com"
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    setErrorMessage('');
                                                }}
                                                disabled={isEmailLoading || isGoogleLoading || isRateLimited}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative w-full">
                                        <PhoneInput
                                            international
                                            withCountryCallingCode
                                            defaultCountry={selectedCountry}
                                            value={phone}
                                            onChange={handlePhoneNumberChange}
                                            className={`premium-phone-input ${errorMessage ? 'error' : ''} ${isPhoneLoading || isDetectingCountry || isGoogleLoading || isRateLimited ? 'disabled' : ''}`}
                                            disabled={isPhoneLoading || isDetectingCountry || isGoogleLoading || isRateLimited}
                                        />
                                    </div>
                                )}
                            </div>

                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                size="invisible"
                                badge="bottomright"
                                style={{
                                  visibility: 'hidden',
                                  position: 'absolute',
                                  left: '-9999px'
                                }}
                                onErrored={() => console.log("reCAPTCHA errored")}
                                onExpired={() => console.log("reCAPTCHA expired")}
                            />

                            <div>
                                <DSButton
                                    type="submit"
                                    variant="cta"
                                    disabled={isEmailLoading || isPhoneLoading || isGoogleLoading || isDetectingCountry || isRateLimited}
                                    title={isRateLimited ? "Please wait before trying again" : isDetectingCountry ? "Detecting location..." : ""}
                                    endIcon={(!isEmailLoading && !isPhoneLoading && !isRateLimited) ? <ArrowRight size={20} /> : undefined}
                                >
                                    {(isEmailLoading || isPhoneLoading) ? (
                                        <Spinner animation="border" size="sm" role="status" />
                                    ) : isRateLimited ? (
                                        `Try again in ${formatTimeRemaining(timeRemaining)}`
                                    ) : (
                                        "Continue"
                                    )}
                                </DSButton>
                            </div>
                        </form>

                        <div className="divider-container">
                            <div className="divider-line"></div>
                            <span className="divider-text">or continue with</span>
                            <div className="divider-line"></div>
                        </div>

                        <div className="social-login-container">
                            <button type="button" className="google-btn-custom" onClick={login}>
                                <svg className="ds-w-5 ds-h-5" style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" fill="#FBBC05"></path>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
                                </svg>
                                {isGoogleLoading ? (
                                    <Spinner animation="border" size="sm" role="status" />
                                ) : (
                                    "Google"
                                )}
                            </button>
                        </div>
                        {errorMessage && (
                            <div style={{ color: '#ef4444', textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', fontWeight: '500' }}>
                                {errorMessage}
                                {isRateLimited && timeRemaining > 0 && (
                                    <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                                        Time remaining: {formatTimeRemaining(timeRemaining)}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="signup-link-container">
                            <p className="signup-text">
                                Don't have an account? <a href="/signup" className="signup-link">Sign up</a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default Login;
